import { toast } from 'vue-sonner'
import type { ApiFavorite } from '@/entities/favorite';
import type { FeedItem } from '@/entities/feed';
import { useClient } from '@/stores/client';
import { useApiFavorite } from '@/composables/api/favorite';
import { useApiResponses } from '@/composables/api/responses';

export type FeedActionKind =
  | 'primary'
  | 'interview'
  | 'question'
  | 'offer'
  | 'favorite'
  | 'more';

type FeedActionTarget = Pick<FeedItem, 'id' | 'type' | 'matchedVacancyId'>;

export type FeedActionSheetMode =
  | { name: 'respond-pick-summary'; item: FeedActionTarget }
  /** No published resume to respond with: finish the draft (`draftId`) or create one. */
  | { name: 'respond-need-summary'; item: FeedActionTarget; draftId: string | null }
  | { name: 'invite'; item: FeedActionTarget; inviteType: 'question' | 'interview' | 'offer' }
  | { name: 'more'; item: FeedActionTarget }
  | { name: 'complaint'; item: FeedActionTarget };

export type PublishedSummary = {
  id: string;
  title: string;
};

const INVITE_KIND_TO_TYPE = {
  question: 'question',
  interview: 'interview',
  offer: 'offer',
} as const;

export function useFeedActions() {
  const client = useClient();
  const apiResponses = useApiResponses();

  const actionSheet = ref<FeedActionSheetMode | null>(null);
  const favoriteState = reactive(new Map<string, string | null>());
  const responseState = reactive(new Map<string, string | null>());
  const busyItems = reactive(new Set<string>());
  const publishedSummaries = ref<PublishedSummary[]>([]);
  let draftSummaryId: string | null = null;
  let favoritesLoaded = false;
  let favoritesLoadPromise: Promise<void> | null = null;

  const isAuthenticated = computed(() => client.isAuthenticated);
  const isEmployer = computed(() => client.getActiveCompany?.id !== undefined && client.getActiveCompany?.id !== null);

  function closeActionSheet(): void {
    actionSheet.value = null;
  }

  function getItemUrl(item: FeedItem): string {
    return item.type === 'vacancy' ? `/vacancies/${item.id}` : `/summaries/${item.id}`;
  }

  function isFavorite(item: FeedItem): boolean {
    return (favoriteState.get(item.id) ?? null) !== null;
  }

  function getFavoriteRecordId(item: FeedItem): string | null {
    return favoriteState.get(item.id) ?? null;
  }

  function hasResponse(item: FeedItem): boolean {
    return (responseState.get(item.id) ?? null) !== null;
  }

  async function toggleFavorite(item: FeedItem): Promise<void> {
    if (!isAuthenticated.value || client.getClientId === null) {
      return;
    }

    if (busyItems.has(item.id)) {
      return;
    }

    busyItems.add(item.id);
    try {
      const apiFavorite = useApiFavorite();

      if (getFavoriteRecordId(item) === null) {
        const id = await apiFavorite.add(item.type, item.id);
        if (id !== null) {
          favoriteState.set(item.id, id);
        }
      } else {
        const deleted = await apiFavorite.delete(item.type, item.id);
        if (deleted) {
          favoriteState.set(item.id, null);
        }
      }
    } finally {
      busyItems.delete(item.id);
    }
  }

  async function fetchPublishedSummaries(): Promise<PublishedSummary[] | null> {
    if (!isAuthenticated.value || client.getClientId === null) {
      return null;
    }

    const response = await mainFetch(`clients/${client.getClientId}/summaries`);
    if (response.error.value) {
      toast.error(apiErrorMessage(response.error.value, 'Не удалось загрузить резюме'));
      return null;
    }
    const data = response.data.value;
    if (!Array.isArray(data?.summaries)) {
      toast.error('Не удалось загрузить резюме');
      return null;
    }

    draftSummaryId = data.summaries.find((summary: { status: number }) => summary.status === 0)?.id ?? null;
    publishedSummaries.value = data.summaries
      .filter((summary: { status: number }) => summary.status === 2)
      .map((summary: { id: string; title: string }) => ({
        id: summary.id,
        title: summary.title,
      }));
    return publishedSummaries.value;
  }

  async function loadFavorites(): Promise<void> {
    if (favoritesLoaded || !isAuthenticated.value || client.getClientId === null) {
      return;
    }

    if (favoritesLoadPromise !== null) {
      return favoritesLoadPromise;
    }

    favoritesLoadPromise = (async () => {
      let allTypesLoaded = true;

      for (const kind of ['summary', 'vacancy'] as const) {
        try {
          const response = await mainFetch('me/favorites', {
            query: { kind, perPage: 100 },
          });
          const items = response.data.value?.items;
          if (response.error.value || !Array.isArray(items)) {
            allTypesLoaded = false;
            continue;
          }

          for (const entry of items as ApiFavorite[]) {
            const target = entry.kind === 'vacancy' ? entry.vacancy : entry.summary;
            favoriteState.set(target.id, entry.favoriteId);
          }
        } catch {
          allTypesLoaded = false;
          // Favorites are best-effort: a failed preload must not block the feed.
        }
      }

      if (allTypesLoaded) {
        favoritesLoaded = true;
      }
    })();

    try {
      await favoritesLoadPromise;
    } finally {
      favoritesLoadPromise = null;
    }
  }

  async function respondWithSummary(item: FeedItem, summaryId: string): Promise<void> {
    if (busyItems.has(item.id)) {
      return;
    }

    busyItems.add(item.id);
    try {
      const response = await apiResponses.add(item.id, summaryId);
      if (response.error.value) {
        toast.error(apiErrorMessage(response.error.value, 'Не удалось откликнуться'));
        return;
      }
      const data = response.data.value;
      responseState.set(item.id, data.response.id as string);
      toast.success('Отклик отправлен');
    } finally {
      busyItems.delete(item.id);
    }
  }

  async function handlePrimary(item: FeedItem): Promise<void> {
    if (item.type === 'vacancy') {
      if (isEmployer.value) {
        toast.error('Компания не может откликаться на вакансии');
        return;
      }
      // A response cannot be cancelled: the CTA reads «Вы откликнулись» and is disabled.
      if (hasResponse(item)) {
        return;
      }
      const summaries = await fetchPublishedSummaries();
      if (summaries === null) return;
      if (summaries.length === 0) {
        actionSheet.value = { name: 'respond-need-summary', item, draftId: draftSummaryId };
        return;
      }
      if (summaries.length === 1) {
        await respondWithSummary(item, summaries[0].id);
        return;
      }
      actionSheet.value = { name: 'respond-pick-summary', item };
      return;
    }

    if (!isEmployer.value) {
      toast.error('Соискатель не может приглашать кандидатов');
      return;
    }
    actionSheet.value = { name: 'invite', item, inviteType: 'question' };
  }

  async function dispatch(kind: FeedActionKind, item: FeedItem): Promise<void> {
    switch (kind) {
      case 'favorite':
        await toggleFavorite(item);
        return;
      case 'more':
        actionSheet.value = { name: 'more', item };
        return;
      case 'primary':
        await handlePrimary(item);
        return;
      case 'interview':
      case 'question':
      case 'offer':
        if (item.type !== 'summary') {
          return;
        }
        if (!isEmployer.value) {
          toast.error('Соискатель не может приглашать кандидатов');
          return;
        }
        actionSheet.value = { name: 'invite', item, inviteType: INVITE_KIND_TO_TYPE[kind] };
    }
  }

  return {
    actionSheet,
    favoriteState,
    responseState,
    busyItems,
    publishedSummaries,
    isAuthenticated,
    isEmployer,
    closeActionSheet,
    dispatch,
    getItemUrl,
    isFavorite,
    getFavoriteRecordId,
    hasResponse,
    toggleFavorite,
    fetchPublishedSummaries,
    loadFavorites,
    respondWithSummary,
  };
}
